import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Sparkles,
    FileText,
    Wand2,
    Settings,
    LogOut,
    Shield,
    Zap,
    FlaskConical,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatureFlag } from "@/contexts/FlagsContext";
import CustomCursor from "@/components/CustomCursor";
import AnimatedBackground from "@/components/AnimatedBackground";
import { motion } from "framer-motion";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/onboarding", label: "Identity Scan", icon: Sparkles, testId: "nav-onboarding" },
    { to: "/report", label: "AI Report", icon: FileText, testId: "nav-report" },
    { to: "/bio-generator", label: "Bio Generator", icon: Wand2, testId: "nav-bio" },
    { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const abEnabled = useFeatureFlag("abTesting");
    const navItems = abEnabled
        ? [
              ...NAV.slice(0, 4),
              { to: "/ab-testing", label: "A/B Testing", icon: FlaskConical, testId: "nav-ab" },
              ...NAV.slice(4),
          ]
        : NAV;

    return (
        <div className="min-h-screen relative" data-testid="dashboard-layout">
            <AnimatedBackground />
            <CustomCursor />

            {/* Sidebar - desktop */}
            <aside
                className="hidden lg:flex fixed left-0 top-0 bottom-0 w-[240px] glass-strong z-40 flex-col rounded-none border-l-0 border-t-0 border-b-0 border-r border-aura-violet/10"
                data-testid="sidebar"
            >
                <div className="px-6 py-7">
                    <div className="flex items-center gap-2.5">
                        <img src="/asset/generated-image__48_-removebg-preview.png" alt="MirrorDigital" style={{ height: 60, width: "auto" }} />
                        <div>
                            <div className="font-syne text-base font-bold tracking-tight text-aura-text">MirrorDigital</div>
                            <div className="font-jetbrains-mono text-[10px] tracking-[0.3em] text-aura-muted -mt-0.5">
                                AURA
                            </div>
                        </div>
                    </div>
                </div>

                <nav className="flex-1 px-3 py-2 space-y-1">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.testId}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2.5 rounded-lg font-dm-sans text-sm transition-all ${
                                    isActive
                                        ? "sidebar-active text-aura-text"
                                        : "text-aura-body hover:text-aura-text hover:bg-aura-elevate"
                                }`
                            }
                        >
                            <item.icon className="w-4 h-4" strokeWidth={1.6} />
                            {item.label}
                        </NavLink>
                    ))}
                    {user?.isAdmin && (
                        <NavLink
                            to="/admin"
                            data-testid="nav-admin"
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2.5 rounded-lg font-dm-sans text-sm transition-all ${
                                    isActive
                                        ? "sidebar-active text-aura-text"
                                        : "text-aura-body hover:text-aura-text hover:bg-aura-elevate"
                                }`
                            }
                        >
                            <Shield className="w-4 h-4" strokeWidth={1.6} />
                            Admin
                        </NavLink>
                    )}
                </nav>

                <div className="p-4 border-t border-aura-violet/10">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-aura-violet to-aura-cyan flex items-center justify-center font-syne text-sm font-bold text-white">
                            {user?.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-dm-sans text-sm truncate text-aura-text">{user?.name}</div>
                            <div className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-violet">
                                {user?.tier}
                            </div>
                        </div>
                    </div>
                    {user?.tier === "FREE" && (
                        <motion.button
                            whileTap={{ scale: 0.97 }}
                            className="w-full text-xs font-dm-sans font-medium px-3 py-2 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white glow-violet-soft btn-ripple mb-2"
                            data-testid="upgrade-cta-btn"
                            onClick={() => navigate("/settings#upgrade")}
                        >
                            Upgrade to Premium
                        </motion.button>
                    )}
                    <button
                        onClick={() => {
                            logout();
                            navigate("/login");
                        }}
                        className="w-full flex items-center gap-2 text-xs text-aura-muted hover:text-aura-text px-3 py-2 transition"
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile bottom nav */}
            <nav
                className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass-strong border-t border-aura-violet/10 flex justify-around py-2 rounded-none"
                data-testid="mobile-nav"
            >
                {navItems.slice(0, 5).map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                            `flex flex-col items-center gap-0.5 px-2 py-1.5 ${
                                isActive ? "text-aura-violet" : "text-aura-muted"
                            }`
                        }
                    >
                        <item.icon className="w-5 h-5" strokeWidth={1.6} />
                        <span className="text-[10px] font-dm-sans">{item.label}</span>
                    </NavLink>
                ))}
            </nav>

            {/* Main */}
            <main className="lg:ml-[240px] pb-20 lg:pb-0 relative z-10">
                <div className="max-w-6xl mx-auto px-5 lg:px-10 py-8 lg:py-12">{children}</div>
            </main>
        </div>
    );
}

